// Canonical-layer overlap registry (North Star gap 8). Declares, for every governed lever family pair, whether
// their dollar contributions can overlap (a de-confliction receipt is REQUIRED before compositing) or are
// structurally disjoint (with the economic basis recorded). The safety property is explicit-or-fail-closed: a pair
// with NO declaration is UNKNOWN, and composites must treat unknown like missing receipts — never as disjoint.
import { buildAggregateAndLensDeclarations } from './lever-overlap-registry.aggregate-and-lens';
import { buildCoreClusterDeclarations } from './lever-overlap-registry.core-clusters';
import { build5571SgaOverlapDeclarations } from './lever-overlap-registry.sga-cost-pool-additions';
export type LeverOverlapRelation = 'overlapping' | 'disjoint';

export type LeverOverlapEvidenceRequirement =
  'structural_policy' | 'deconfliction_receipt' | 'immutable_zero_overlap_receipt';

export type LeverOverlapDeclaration = Readonly<{
  familyA: string;
  familyB: string;
  relation: LeverOverlapRelation;
  evidenceRequirement: LeverOverlapEvidenceRequirement;
  basis: string;
}>;

export type LeverOverlapPair = Readonly<{ familyA: string; familyB: string }>;

export type LeverOverlapReceiptRef = Readonly<{
  familyA: string;
  familyB: string;
  receiptClaimId: string;
}>;

export type LeverOverlapReceiptCheck = Readonly<{
  requiredPairs: readonly LeverOverlapPair[];
  unknownPairs: readonly LeverOverlapPair[];
  missingReceiptPairs: readonly LeverOverlapPair[];
  ok: boolean;
}>;

const LABOR_COST_FAMILIES = [
  'delayering',
  'span_of_control_rightsizing',
  'workforce_reduction',
  'productivity_lean_ai',
  // Role relocation / shoring reduces the same internal labor cost pools, so it
  // overlaps the other labor levers and is withheld from the additive bridge.
  'labor_arbitrage',
] as const;

// Revenue-side levers all act on the same customer/lane revenue line, so every
// pair overlaps and is gated out of the additive bridge without a receipt.
const REVENUE_SIDE_FAMILIES = [
  'pricing_uplift',
  'discount_leakage',
  'revenue_mix_shift',
  'growth_cross_sell',
  'churn_reduction',
] as const;

// Buy-side purchased-transportation levers. Carrier renegotiation, network design, capacity utilization, mode mix,
// and fuel/accessorial recovery all attack the SAME ~$13B purchased-transportation spend, so they overlap each
// other and must not be additively summed without a de-confliction receipt.
const PURCHASED_TRANSPORT_FAMILIES = [
  'carrier_renegotiation',
  'network_optimization',
  'capacity_utilization',
  'mode_mix_shift',
  'fuel_efficiency',
] as const;

const SEGMENT_LENS_FAMILY = 'segment_margin_cleanup';

// Cargo-claims loss recovery: claims-expense GL dollars (5300_cargo_claims_expense), scoped by carrier/lane
// members. Its pairs with other dollar families resolve on the member-proof path (shared gl_account / lane fixed
// keys measured from the governed register), NOT by declaration — except the segment lens (overlapping, like every
// dollar family), the certified SG&A aggregate (disjoint with `immutable_zero_overlap_receipt`), and
// productivity_lean_ai (receipt-required 'overlapping' posture — see the #4008 S9 declaration below; the policy
// row is never evidence and publication still requires the cut-bound receipt over the exact member universes).
const RISK_CLAIMS_FAMILY = 'risk_claims';

// The certified SG&A aggregate family (module sga-spend): ONE module-owned
// bridge claim that replaces its eight cost-pool leaf families in cross-module
// composition. It draws from exactly the granular vendor pools below, so it
// inherits their disjoint posture against the other clusters and must never be
// summed with its own leaves.
const SGA_AGGREGATE_FAMILY = 'sga_spend';

// Cost-to-serve account repricing (North Star gap 8, misroute fix #2411). Repricing the worst / unprofitable
// accounts recovers margin on the SAME customer-account revenue realization line as the revenue-side pricing
// levers, drawing on the same gl_account revenue pools — so it overlaps every revenue-side family and is withheld
// from the additive bridge without a de-confliction receipt. The account-level gl_account scope is the shared
// comparable dimension (the universal headroom path). Against internal-labor, purchased-transport, and non-labor
// SG&A vendor pools it draws on structurally different gl_accounts and is disjoint. Registering it removes the
// fail-closed UNKNOWN status that previously kept a value-gap composite from admitting/de-conflicting CTS
// recovery.
//
// SCOPE: this family covers account REPRICING only (revenue-side margin recovery). A future cost-side
// cost-to-serve lever (e.g. service-cost reduction on the same accounts) draws on cost-pool gl_accounts and would
// invalidate the disjoint declarations below — it must be registered as its own family, never mapped onto this
// one.
const COST_TO_SERVE_FAMILY = 'cost_to_serve';

// The non-workforce org-cost initiative pool (module org-analysis-optimization, claim
// org_cost_sub_lever_net_run_rate_contribution_usd). Historically this claim rode the `delayering` binding, so it
// inherited the labor cluster's "internal labor cost" disjoint declarations — a VACUOUS basis for a claim whose
// dollars are explicitly NON-workforce initiatives (deltaHeadcount 0): operating-model initiative dollars can
// absolutely draw on the same vendor / cost pools as SG&A spend. Its member keys (org_cost_scope:*, org-state:*,
// bare initiative ids) are incommensurable with every other family's fixed keys (cost_pool:*, gl_account:*, lane
// ids), so member-level disjointness can never be proven either. It is therefore registered as its OWN family with
// EVERY pair declared 'overlapping': org-cost dollars are never additive to any other family without an explicit
// approved exclusion receipt. This is the fence that keeps an unproven org-cost claim out of the certified floor.
export const ORG_COST_FAMILY = 'org_cost';

// Non-labor SG&A spend families: vendor/expense dollars, not internal labor
// cost, not purchased transportation, not revenue realization.
const SGA_SPEND_FAMILIES = [
  'travel_and_expense',
  'indirect_procurement',
  'professional_fees',
  'tech_software',
  'real_estate_footprint',
  'shared_services',
  'marketing_spend',
  // Contingent-labor / outsourced-services vendor spend is a distinct SG&A
  // category, disjoint from the others and additive to the bridge.
  'outside_contractors',
] as const;

// The eight non-labor SG&A cost-pool leaf families that the certified sga_spend
// aggregate replaces one-for-one in cross-module composition (see
// SGA_AGGREGATE_FAMILY overlaps below). Exported so the composite-EBITDA bridge
// roster can represent them through the aggregate (module sga-spend) and exclude
// them as standalone bridge members — summing an aggregate with its own leaves
// double-counts the same vendor pools.
export const SGA_AGGREGATE_LEAF_FAMILY_IDS: readonly string[] = SGA_SPEND_FAMILIES;

function buildDeclarations(): readonly LeverOverlapDeclaration[] {
  const declarations: LeverOverlapDeclaration[] = [];

  declarations.push(
    ...buildCoreClusterDeclarations({
      laborCostFamilies: LABOR_COST_FAMILIES,
      revenueSideFamilies: REVENUE_SIDE_FAMILIES,
      purchasedTransportFamilies: PURCHASED_TRANSPORT_FAMILIES,
      sgaSpendFamilies: SGA_SPEND_FAMILIES,
    })
  );

  declarations.push(
    ...buildAggregateAndLensDeclarations({
      laborCostFamilies: LABOR_COST_FAMILIES,
      revenueSideFamilies: REVENUE_SIDE_FAMILIES,
      purchasedTransportFamilies: PURCHASED_TRANSPORT_FAMILIES,
      sgaSpendFamilies: SGA_SPEND_FAMILIES,
      segmentLensFamily: SEGMENT_LENS_FAMILY,
      riskClaimsFamily: RISK_CLAIMS_FAMILY,
      sgaAggregateFamily: SGA_AGGREGATE_FAMILY,
      costToServeFamily: COST_TO_SERVE_FAMILY,
      orgCostFamily: ORG_COST_FAMILY,
    })
  );

  // #5571 P1 remediation: 14 new SG&A families had no declared relation - see the additions file.
  declarations.push(
    ...build5571SgaOverlapDeclarations([
      ...LABOR_COST_FAMILIES, ...REVENUE_SIDE_FAMILIES, ...PURCHASED_TRANSPORT_FAMILIES,
      ...SGA_SPEND_FAMILIES, SEGMENT_LENS_FAMILY, RISK_CLAIMS_FAMILY, SGA_AGGREGATE_FAMILY,
      COST_TO_SERVE_FAMILY, ORG_COST_FAMILY,
    ])
  );

  return Object.freeze(declarations);
}

const LEVER_OVERLAP_DECLARATIONS = buildDeclarations();

function pairKey(familyA: string, familyB: string): string {
  return [familyA, familyB].sort().join('::');
}

const DECLARATION_BY_PAIR = new Map<string, LeverOverlapDeclaration>(
  LEVER_OVERLAP_DECLARATIONS.map((item) => [pairKey(item.familyA, item.familyB), item])
);

export function listLeverOverlapDeclarations(): readonly LeverOverlapDeclaration[] {
  return LEVER_OVERLAP_DECLARATIONS;
}

export function declaredOverlapRelation(
  familyA: string,
  familyB: string
): LeverOverlapDeclaration | null {
  if (familyA === familyB) return null;
  return DECLARATION_BY_PAIR.get(pairKey(familyA, familyB)) ?? null;
}

export function requiredOverlapPairs(familyIds: readonly string[]): readonly LeverOverlapPair[] {
  const unique = Array.from(new Set(familyIds.filter((id) => id.trim().length > 0)));
  const pairs: LeverOverlapPair[] = [];
  for (let i = 0; i < unique.length; i += 1) {
    for (let j = i + 1; j < unique.length; j += 1) {
      const declared = declaredOverlapRelation(unique[i], unique[j]);
      if (
        declared &&
        declared.evidenceRequirement !== 'structural_policy'
      ) {
        pairs.push(Object.freeze({ familyA: unique[i], familyB: unique[j] }));
      }
    }
  }
  return Object.freeze(pairs);
}

export function unknownOverlapPairs(familyIds: readonly string[]): readonly LeverOverlapPair[] {
  const unique = Array.from(new Set(familyIds.filter((id) => id.trim().length > 0)));
  const pairs: LeverOverlapPair[] = [];
  for (let i = 0; i < unique.length; i += 1) {
    for (let j = i + 1; j < unique.length; j += 1) {
      if (declaredOverlapRelation(unique[i], unique[j]) === null) {
        pairs.push(Object.freeze({ familyA: unique[i], familyB: unique[j] }));
      }
    }
  }
  return Object.freeze(pairs);
}

export function checkLeverOverlapReceipts(
  familyIds: readonly string[],
  receipts: readonly LeverOverlapReceiptRef[]
): LeverOverlapReceiptCheck {
  const requiredPairs = requiredOverlapPairs(familyIds);
  const unknownPairs = unknownOverlapPairs(familyIds);
  const receiptKeys = new Set(
    receipts
      .filter((receipt) => receipt.receiptClaimId.trim().length > 0)
      .map((receipt) => pairKey(receipt.familyA, receipt.familyB))
  );
  const missingReceiptPairs = requiredPairs.filter(
    (pair) => !receiptKeys.has(pairKey(pair.familyA, pair.familyB))
  );
  return Object.freeze({
    requiredPairs,
    unknownPairs,
    missingReceiptPairs,
    ok: missingReceiptPairs.length === 0 && unknownPairs.length === 0,
  });
}
